import { HttpClient } from "../api/HttpClient";
import { BaseAuthProvider, type BaseAuthProviderConfig } from "./BaseAuthProvider";
import type { AuthEndpoints, Credentials, PasswordCredentials, Tokens, User } from "../types";

export interface JwtProviderConfig extends BaseAuthProviderConfig {
  baseUrl: string;
  endpoints?: Partial<AuthEndpoints>;
  defaultTimeoutMs?: number;
}

interface RawTokenResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number; // seconds
  expiresAt?: number; // epoch ms — takes precedence over expiresIn if both present
  user?: User;
}

function toTokens(raw: RawTokenResponse): Tokens {
  return {
    accessToken: raw.accessToken,
    refreshToken: raw.refreshToken,
    expiresAt: raw.expiresAt ?? (raw.expiresIn !== undefined ? Date.now() + raw.expiresIn * 1000 : undefined),
  };
}

/**
 * Username/password authentication against a first-party JSON backend that
 * issues short-lived access tokens plus a rotating refresh token.
 */
export class JwtProvider extends BaseAuthProvider {
  private readonly endpoints: AuthEndpoints;
  private readonly http: HttpClient;

  constructor(private readonly config: JwtProviderConfig) {
    super(config);
    this.endpoints = {
      login: "/login",
      logout: "/logout",
      refresh: "/refresh",
      currentUser: "/me",
      forgotPassword: "/forgot-password",
      resetPassword: "/reset-password",
      verifyEmail: "/verify-email",
      changePassword: "/change-password",
      ...config.endpoints,
    };
    this.http = new HttpClient({
      baseUrl: config.baseUrl,
      defaultTimeoutMs: config.defaultTimeoutMs,
      getAccessToken: () => this.getAccessToken(),
      refreshAccessToken: async () => {
        await this.refresh();
        return this.getAccessToken();
      },
      onAuthFailure: () => void this.logout(),
    });
  }

  protected async doLogin(credentials: Credentials): Promise<{ tokens: Tokens; user?: User | null }> {
    const creds = credentials as PasswordCredentials;
    const raw = await this.http.request<RawTokenResponse>(this.endpoints.login!, {
      method: "POST",
      skipAuth: true,
      body: { identifier: creds.identifier, password: creds.password, rememberMe: creds.rememberMe },
    });
    return { tokens: toTokens(raw), user: raw.user ?? undefined };
  }

  protected async doRefresh(): Promise<Tokens> {
    const current = this.session.getTokens();
    const raw = await this.http.request<RawTokenResponse>(this.endpoints.refresh!, {
      method: "POST",
      skipAuth: true,
      skipAuthRetry: true,
      body: { refreshToken: current?.refreshToken },
    });
    return toTokens(raw);
  }

  protected async doFetchCurrentUser(): Promise<User | null> {
    try {
      return await this.http.request<User>(this.endpoints.currentUser!, { method: "GET" });
    } catch {
      return null;
    }
  }

  protected async doLogout(): Promise<void> {
    if (!this.endpoints.logout) return;
    const current = this.session.getTokens();
    await this.http.request(this.endpoints.logout, {
      method: "POST",
      skipAuthRetry: true,
      body: { refreshToken: current?.refreshToken },
    });
  }

  // --- Convenience flows beyond the core AuthProvider contract ---

  async forgotPassword(identifier: string): Promise<void> {
    await this.http.request(this.endpoints.forgotPassword!, { method: "POST", skipAuth: true, body: { identifier } });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await this.http.request(this.endpoints.resetPassword!, {
      method: "POST",
      skipAuth: true,
      body: { token, newPassword },
    });
  }

  async verifyEmail(token: string): Promise<void> {
    await this.http.request(this.endpoints.verifyEmail!, { method: "POST", skipAuth: true, body: { token } });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await this.http.request(this.endpoints.changePassword!, {
      method: "POST",
      body: { currentPassword, newPassword },
    });
  }
}
